import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IconButton, Tooltip} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import VariableService from '../../../services/VariableService/VariableService';
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from '@material-ui/icons/Check';
import {CSSTransition} from 'react-transition-group';
import TextField from "@material-ui/core/TextField";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {changedFields, initialActions, saveAvailable} from "../../../recoil-store/recoil-state";
import {CheckIfFlowUpdated} from "../../../helpers/helper";

import './dbtable.css';

export default function DBTable({
                                    action,
                                    options,
                                    automVersionId,
                                    inputName,
                                    isRequired,
                                    setOpenVariable,
                                    isActiveDirectory = false,
                                    formData,
                                    automId,
                                    headerWithVariable
                                }) {
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const [variable, setVariable] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const {t} = useTranslation();
    const [isControl, setIsControl] = useState([]);
    const [expandedItems, setExpandedItems] = useState({});
    const [isEdit, setIsEdit] = useState({});
    const [fields, setFields] = useState(action.fields);
    const [tempValues, setTempValues] = useState({});
    const tableRef = useRef(null);

    const tableLabel = "CONTENT.CONFIGURATION-FORM.TABLE.";


    useEffect(() => {
        let y = Object.keys(action.fields)
        if (y.indexOf(inputName) === -1) {
            if (inputName !== undefined) {
                action.fields[inputName] = {};
                let x = Object.keys(action.fields[inputName])
                if (x.indexOf(inputName) === -1) {
                    action.fields[inputName] = {type: "TABLE", value: [], isRequired};
                }
            }
        }

    }, [fields])

    useEffect(() => {
        if (refresh) {
            VariableService.getVariable(automVersionId)
                .then((response) => {
                    if (response.data) {
                        if (Array.isArray(response.data)) {
                            setVariable(response.data)
                        } else {
                            setVariable([response.data])
                        }
                    } else {
                        setVariable([])
                    }
                })
                .catch((error) => {
                    //err
                });
            setRefresh(false);
        }
    }, [refresh]);

    useEffect(() => {
        let y = Object.keys(action.fields)
        setIsControl(y);
    }, [inputName])


    useEffect(() => {
        let updateFields = {...fields};
        if (!fields[inputName]) {
            updateFields[inputName] = {type: "TABLE", value: [], isRequired};
            setFields(updateFields);
        }
    }, [fields, inputName, isRequired]);

    const handleChange = (index, property, event) => {
        const value = event ? event.target.value : ""
        if (!hasOnlyWhiteSpace(value)) {
            return
        }
        // Update the temporary values state to even include empty strings
        setTempValues(prev => ({
            ...prev,
            [`${property}.${index}`]: value
        }));
    };

    const handleSave = (index, property, e) => {
        if (e?.relatedTarget && e.relatedTarget.className?.includes('close-editable-input')) {
            e.preventDefault();
            return;
        }
        // Only save if there's a meaningful change
        if (tempValues.hasOwnProperty(`${property}.${index}`) && tempValues[`${property}.${index}`] !== "") {
            const newFields = {...fields};
            newFields[inputName].value[index][property] = tempValues[`${property}.${index}`];
            setFields(newFields);
            setIsEdit(prev => ({...prev, [`${property}.${index}`]: false}));
            setTempValues(prev => {
                const newState = {...prev};
                delete newState[`${property}.${index}`];  // Clean up temporary value
                return newState;
            });
            const field = initialActionsData[action.id]?.fields["value"];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated("value", action, oldValue, tempValues[`${property}.${index}`], setSaveAvailable, setChangeFields, changedFieldsState);
        }
    };

    const handleRevert = (index, property) => {
        // Revert changes by deleting the temp value
        setTempValues(prev => {
            const newState = {...prev};
            delete newState[`${property}.${index}`];  // Ensure to clear out any temp entry
            return newState;
        });
        setIsEdit(prev => ({...prev, [`${property}.${index}`]: false}));
    };

    const handleKeyDown = (event, index, property) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSave(index, property);
        } else if (event.key === 'Escape') {
            handleRevert(index, property);
        }
    };

    const hasOnlyWhiteSpace = (value) => {
        if (value.includes(" ")) {
            return value.trim().length;
        }

        return true;
    }

    const nbspArrayComponent = (value, isAvailable) => {
        if (!isAvailable) return false;
        let nbspArray = [];
        const valueHasOnlyWhitespace = hasOnlyWhiteSpace(value);
        if (!valueHasOnlyWhitespace) {
            for (let i = 0; value.length > i; i++) {
                nbspArray.push("&nbsp;")
            }

            const spaceElements = nbspArray.map(space => '\u00A0');

            return (
                <>
                    {spaceElements}
                </>
            )
        }

        return value;
    };

    const handleDelete = (index) => {
        const newFields = {...fields};
        const oldValue = !newFields ? undefined : newFields.value;
        newFields[inputName].value.splice(index, 1);
        setFields(prevFields => {
            return {
                ...prevFields,
                [inputName]: {
                    ...prevFields[inputName],
                    newFields
                }
            };
        });

        CheckIfFlowUpdated("value", action, oldValue, newFields.value, setSaveAvailable, setChangeFields, changedFieldsState);
    };

    const handleNewRow = () => {
        const newFields = {...fields};
        const lengthArray = newFields[inputName].value.length
        newFields[inputName].value.push({name: "", value: ""});
        setFields(prevFields => {
            return {
                ...prevFields,
                [inputName]: {
                    ...prevFields[inputName],
                    newFields
                }
            };
        });

        handleChange(lengthArray, "name", undefined)
        handleChange(lengthArray, "value", undefined)
    };


    return (
        <div style={{width: '100%'}}>
            <div className="data-table-container-area">
                <div className='table-area'>
                    <IconButton className='add-column-icon-button' onClick={() => handleNewRow()}>
                        <AddCircleOutlineIcon className="add-column-icon" />
                    </IconButton>
                    <table className="table" ref={tableRef}>
                        <thead>
                            <tr>
                                {options?.headers?.map((item, itemI) =>
                                    <th key={itemI}>{t(item)}</th>
                                )}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>

                        {(action.fields !== undefined && isControl.indexOf(inputName) !== -1 && Array.isArray(action.fields[inputName]?.value)) && action.fields[inputName].value.map((item, rowIndex) => {
                            const key = `${rowIndex}-${new Date().getTime()}`;
                            return <tr key={key}>
                                <td className={"table-cell"} style={{position: 'relative'}}>
                                    {isEdit[`name.${rowIndex}`] ? (
                                        <>
                                            <TextField
                                                fullWidth
                                                autoFocus
                                                type="text"
                                                value={tempValues.hasOwnProperty(`name.${rowIndex}`) ? tempValues[`name.${rowIndex}`] : item.name}
                                                onChange={(e) => handleChange(rowIndex, "name", e)}
                                                onBlur={(e) => handleSave(rowIndex, "name", e)}  // Consider checking if save is appropriate here
                                                onKeyDown={(e) => handleKeyDown(e, rowIndex, "name")}
                                                error={!tempValues[`name.${rowIndex}`] && tempValues[`name.${rowIndex}`] !== undefined}
                                                helperText={!tempValues[`name.${rowIndex}`] && tempValues[`name.${rowIndex}`] !== undefined ? "This field cannot be empty." : ""}
                                                style={{borderColor: !tempValues[`name.${rowIndex}`] || tempValues[`name.${rowIndex}`].trim().length === 0 ? 'red' : ''}}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                right: 5,
                                                textAlign: 'center',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                zIndex: 2,
                                                top: 15,
                                                maxWidth: 35
                                            }}>
                                                <CSSTransition
                                                    in={isEdit[`name.${rowIndex}`]}
                                                    timeout={300}
                                                    classNames="icon-slide"
                                                    unmountOnExit
                                                >
                                                    <IconButton
                                                        disabled={!tempValues[`name.${rowIndex}`]}
                                                        onClick={() => tempValues[`name.${rowIndex}`] !== "" && handleSave(rowIndex, "name")}
                                                        style={{
                                                            width: '10px',
                                                            height: '10px',
                                                            backgroundColor: '#74d974',
                                                            margin: '0 1px',
                                                            border: '1px solid #0aab0a',
                                                            color: '#ffffff',
                                                            borderRadius: 0,
                                                            padding: 8
                                                        }}
                                                        className={`${!tempValues['name.' + rowIndex] ? "disabled" : ""}`}
                                                    >
                                                        <CheckIcon
                                                            style={{fontSize: '15px'}}/>
                                                    </IconButton>
                                                </CSSTransition>
                                                <CSSTransition
                                                    in={isEdit[`name.${rowIndex}`]}
                                                    timeout={300}
                                                    classNames="icon-slide"
                                                    unmountOnExit
                                                >
                                                    <IconButton
                                                        className={"close-editable-input"}
                                                        style={{
                                                            width: '10px',
                                                            height: '10px',
                                                            backgroundColor: '#e38383',
                                                            margin: '0 6px 0 1px',
                                                            color: '#ffffff',
                                                            border: '1px solid #c12929',
                                                            borderRadius: 0,
                                                            padding: 8
                                                        }}
                                                        onClick={() => handleRevert(rowIndex, "name")}
                                                    >
                                                        <CloseIcon
                                                            style={{fontSize: '15px', width: '15px', height: '15px'}}/>
                                                    </IconButton>
                                                </CSSTransition>
                                            </div>
                                        </>
                                    ) : (
                                        <Tooltip title={t("EDIT")} placement="top">
                                            {item.name?.length ? (<span className={"table-span"}
                                                                        onClick={() => setIsEdit(prev => ({
                                                                            ...prev,
                                                                            [`name.${rowIndex}`]: true
                                                                        }))}>
                                        {item.name}
                                    </span>) :
                                                (<span className={"table-span"}
                                                       onClick={() => setIsEdit(prev => ({
                                                           ...prev,
                                                           [`name.${rowIndex}`]: true
                                                       }))}>
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    </span>)
                                            }
                                        </Tooltip>
                                    )}

                                </td>
                                <td className={"table-cell"} style={{position: 'relative'}}>
                                    {isEdit[`value.${rowIndex}`] ? (
                                        <>
                                            <TextField
                                                fullWidth
                                                autoFocus
                                                type="text"
                                                value={tempValues.hasOwnProperty(`value.${rowIndex}`) ? tempValues[`value.${rowIndex}`] : item.value}
                                                onChange={(e) => handleChange(rowIndex, "value", e)}
                                                onBlur={(e) => handleSave(rowIndex, "value", e)}  // Consider checking if save is appropriate here
                                                onKeyDown={(e) => handleKeyDown(e, rowIndex, "value")}
                                                error={!tempValues[`value.${rowIndex}`] && tempValues[`value.${rowIndex}`] !== undefined}
                                                helperText={!tempValues[`value.${rowIndex}`] && tempValues[`value.${rowIndex}`] !== undefined ? "This field cannot be empty." : ""}
                                                style={{borderColor: !tempValues[`value.${rowIndex}`] || tempValues[`value.${rowIndex}`].trim().length === 0 ? 'red' : ''}}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                right: 5,
                                                textAlign: 'center',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                zIndex: 2,
                                                top: 15,
                                                maxWidth: 35
                                            }}>
                                                <CSSTransition
                                                    in={isEdit[`value.${rowIndex}`]}
                                                    timeout={300}
                                                    classNames="icon-slide"
                                                    unmountOnExit
                                                >
                                                    <IconButton
                                                        disabled={!tempValues[`value.${rowIndex}`]}
                                                        onClick={() => handleSave(rowIndex, "value")}
                                                        style={{
                                                            width: '10px',
                                                            height: '10px',
                                                            backgroundColor: '#74d974',
                                                            margin: '0 1px',
                                                            border: '1px solid #0aab0a',
                                                            color: '#ffffff',
                                                            borderRadius: 0,
                                                            padding: 8
                                                        }}
                                                        className={`${!tempValues['value.' + rowIndex] ? "disabled" : ""}`}
                                                    >
                                                        <CheckIcon style={{fontSize: '15px'}}/>
                                                    </IconButton>
                                                </CSSTransition>
                                                <CSSTransition
                                                    in={isEdit[`value.${rowIndex}`]}
                                                    timeout={300}
                                                    classNames="icon-slide"
                                                    unmountOnExit
                                                >
                                                    <IconButton
                                                        className={"close-editable-input"}
                                                        style={{
                                                            width: '10px',
                                                            height: '10px',
                                                            backgroundColor: '#e38383',
                                                            margin: '0 6px 0 1px',
                                                            color: '#ffffff',
                                                            border: '1px solid #c12929',
                                                            borderRadius: 0,
                                                            padding: 8
                                                        }}
                                                        onClick={() => handleRevert(rowIndex, "value")}
                                                    >
                                                        <CloseIcon style={{fontSize: '15px'}}/>
                                                    </IconButton>
                                                </CSSTransition>
                                            </div>
                                        </>
                                    ) : (
                                        <Tooltip title={t("EDIT")} placement="top">
                                            {item.value?.length ? (<span className={"table-span"}
                                                                         onClick={() => setIsEdit(prev => ({
                                                                             ...prev,
                                                                             [`value.${rowIndex}`]: true
                                                                         }))}>
                                       {nbspArrayComponent(item.value, true)}
                                    </span>) :
                                                (<span className={"table-span"}
                                                       onClick={() => setIsEdit(prev => ({
                                                           ...prev,
                                                           [`value.${rowIndex}`]: true
                                                       }))}>
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    </span>)
                                            }
                                        </Tooltip>
                                    )}
                                </td>
                                <td style={{cursor: "pointer", width: "2rem"}} onClick={() => handleDelete(rowIndex)}>
                                    <DeleteIcon style={{width: "20px", height: "20px", marginTop: "0.2rem"}}/>
                                </td>
                            </tr>

                        })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
